import React, { useState, useEffect, Fragment } from 'react'
import { Col, Row, Table, Button, Spinner, Card } from 'react-bootstrap'
import Select from 'react-select'
import formatCPF from '@brazilian-utils/format-cpf'
import { format } from 'date-fns'

import colaboradoresService from '../../services/colaboradores.service'
import RelatorioService from '../../services/relatorio.service'
import { formatMoney } from '../../utils/string.utils'
import PageLoading from '../Common/PageLoading'
import Paginator from '../Common/Paginator'
import CSVDownload from './CSVDownload'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons'

const montarFiltros = (selectedEspecialidade) => {
  const filtros = []
  if (selectedEspecialidade) {
    filtros.push({ campo: 'especialidadeId', valor: selectedEspecialidade.value })
  }
  return filtros
}

const montarLinkPaciente = (pacienteId) => {
  if (!pacienteId) return '-'
  return `${window.location.origin}/pacientes/${pacienteId}`
}

export default function PacientesDevedores({ selectedRelatorio }) {
  const itensPorPagina = 20

  const [isLoading, setLoading] = useState(false)
  const [isLoadingCSV, setLoadingCSV] = useState(false)
  const [itensLista, setItensLista] = useState([])
  const [cabecalhoRelatorio] = useState(['Nome', 'CPF', 'Telefone', 'Saldo Devedor', 'Link do Paciente'])
  const [pagina, setPagina] = useState(0)
  const [totalPaginas, setTotalPaginas] = useState(0)
  const [selectedEspecialidade, setSelectedEspecialidade] = useState(null)
  const [especialidadeOptions, setEspecialidadeOptions] = useState([])
  const [filterLoading, setFilterLoading] = useState(false)
  const [resumo, setResumo] = useState(null)

  useEffect(() => {
    didMount()
  }, [])

  useEffect(() => {
    handleSearch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagina])

  const didMount = async () => {
    setFilterLoading(true)
    const { data } = await colaboradoresService.getAllEspecialidades()
    setEspecialidadeOptions(
      data.map(({ nome, id }) => ({
        label: nome,
        value: id,
      }))
    )
    setFilterLoading(false)
  }

  const relatorioItem = (item) => ({
    nomePaciente: item.nomePaciente ? item.nomePaciente.toUpperCase() : '-',
    cpf: item.cpf ? formatCPF(item.cpf) : '-',
    telefone: item.telefone || '-',
    saldoDevedor: item.saldoDevedor != null ? formatMoney(item.saldoDevedor) : '-',
    linkPaciente: montarLinkPaciente(item.pacienteId),
  })

  const formatarValorResumo = (descricao, valor) => {
    if (!valor) return descricao?.toLowerCase().includes('valor') ? formatMoney(0) : '0'
    return descricao?.toLowerCase().includes('valor') ? valor : valor
  }

  const getValuesLista = async () => {
    setLoadingCSV(true)
    try {
      const filtros = montarFiltros(selectedEspecialidade)
      const response = await RelatorioService.getRegistrosDevedores(filtros, 1, 999999)
      const dados = response?.lista?.dados ?? []

      const valores = dados.map((item) => Object.values(relatorioItem(item)))

      return {
        data: valores,
        headers: cabecalhoRelatorio,
        filename: `${selectedRelatorio.label} - ${format(new Date(), 'dd-MM-yyyy')}.csv`,
      }
    } finally {
      setLoadingCSV(false)
    }
  }

  const handleSearch = async () => {
    setLoading(true)
    try {
      const filtros = montarFiltros(selectedEspecialidade)
      const { lista, resumo } = await RelatorioService.getRegistrosDevedores(filtros, pagina + 1, itensPorPagina)
      setItensLista(lista?.dados ?? [])
      setTotalPaginas(lista?.totalPaginas ?? 0)
      setResumo(resumo)
    } catch (error) {
      console.error(error)
    }
    setLoading(false)
  }

  const handleClear = () => {
    setSelectedEspecialidade(null)
    setPagina(0)
  }

  const handleChangeEspecialidade = (option) => {
    setSelectedEspecialidade(option)
    setPagina(0)
  }

  const handlePagina = (data) => setPagina(data.selected)

  const renderItens = () =>
    itensLista?.length > 0 ? (
      <Fragment>
        <Card className='mb-4 py-2 mt-4'>
          <Row>
            {resumo &&
              resumo.map(({ descricao, valor }, index) => (
                <Col key={index} className='d-flex align-items-center justify-content-center flex-column'>
                  <small className='text-center text-muted m-0'>{!!descricao ? descricao.toUpperCase() : '-'}</small>
                  <h4 className='text-center m-0 font-weight-bold'>{formatarValorResumo(descricao, valor)}</h4>
                </Col>
              ))}
          </Row>
        </Card>

        <Table hover size='sm' className='border rounded-0 shadow-sm' style={{ backgroundColor: 'white' }}>
          <thead>
            <tr className='text-center'>
              <th width='180px' className='text-left'>Nome</th>
              <th width='120px'>CPF</th>
              <th width='160px'>Telefone</th>
              <th width='110px'>Saldo Devedor</th>
              <th width='100px'>Detalhe</th>
            </tr>
          </thead>
          <tbody>
            {itensLista.map((item, index) => {
              const { pacienteId, nomePaciente, cpf, telefone, saldoDevedor } = item

              return (
                <tr key={index} className='text-center' style={{ fontSize: 'smaller' }}>
                  <td className='text-left'>{nomePaciente ? nomePaciente.toUpperCase() : '-'}</td>
                  <td>{cpf ? formatCPF(cpf) : '-'}</td>
                  <td>{telefone || '-'}</td>
                  <td>{saldoDevedor != null ? formatMoney(saldoDevedor) : '-'}</td>
                  <td>
                    {pacienteId ? (
                      <a
                        href={`/pacientes/${pacienteId}`}
                        target='_blank'
                        rel='noopener noreferrer'
                        style={{ color: '#007bff', textDecoration: 'none', cursor: 'pointer', fontWeight: '500' }}
                        onMouseEnter={(e) => (e.target.style.textDecoration = 'underline')}
                        onMouseLeave={(e) => (e.target.style.textDecoration = 'none')}
                        title='Abrir cadastro do paciente em nova aba'
                      >
                        Ver paciente <FontAwesomeIcon icon={faExternalLinkAlt} size='xs' style={{ marginLeft: '5px', opacity: 0.7 }} />
                      </a>
                    ) : (
                      '-'
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </Table>
        {totalPaginas > 1 && <Paginator handlePagina={handlePagina} pageCount={totalPaginas} paginaAtual={pagina} />}
      </Fragment>
    ) : (
      <div className='mt-4'>Nenhum registro encontrado</div>
    )

  const renderLoading = () => <PageLoading />

  return (
    <Fragment>
      <Row>
        <Col md='3'>
          <label>Especialidade</label>
          <Select
            placeholder='Especialidade'
            className='w-100 mr-1'
            onChange={handleChangeEspecialidade}
            value={selectedEspecialidade}
            options={especialidadeOptions}
          />
        </Col>
        <Col>
          {filterLoading && <Spinner animation='border' variant='primary' size='sm' style={spinnerStyle} />}
          <Button disabled={filterLoading} onClick={handleClear} variant='outline-danger' className='mr-2' style={buttonCleanSearch}>
            Limpar
          </Button>
          {filterLoading && <Spinner animation='border' variant='primary' size='sm' />}
          <Button disabled={filterLoading} onClick={handleSearch} variant='primary' className='mr-2' style={buttonSearch}>
            Pesquisar
          </Button>
          <CSVDownload asyncExportMethod={getValuesLista}>
            <Button
              disabled={filterLoading || isLoadingCSV}
              variant='outline-secondary'
              className='mr-2'
              style={buttonExportCsv}
            >
              {isLoadingCSV ? <Spinner animation='border' size='sm' variant='light' /> : 'Gerar CSV'}
            </Button>
          </CSVDownload>
        </Col>
      </Row>
      {isLoading ? renderLoading() : renderItens()}
    </Fragment>
  )
}

const spinnerStyle = {
  position: 'absolute',
  top: '10px',
  right: '50px',
}

const buttonCleanSearch = {
  position: 'absolute',
  bottom: '0',
  height: '38px',
}

const buttonSearch = {
  position: 'absolute',
  bottom: '0',
  left: '100px',
  height: '38px',
}

const buttonExportCsv = {
  position: 'absolute',
  bottom: '0',
  left: '220px',
  height: '38px',
}
